import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { ISSUE_SEVERITY } from "contracts/enums/core";
import { CORE_WEB_VITALS_CONFIDENCE_LEVEL } from "contracts/enums/web-vitals";
import { MAP_CHANGE_STATUS, MAP_CHANGE_TYPE } from "../../enums/map";
import { clusterContextForPages } from "../scan/scanModel";
import {
  consolidateChange,
  createHubChange,
  improvePatternChange,
  reorganiseGroupChange,
  unresolvedChange,
} from "./mapChangeBuilders";
import {
  affectedFactsForIssues,
  brokenOutboundLinks,
  factsForPages,
  groupByRole,
} from "./mapChangeSelection";
import {
  groupNeedsClientContext,
  hasSectionHub,
  relatedGroupLabels,
} from "./mapDerivation";
import { clarifyDestinationChange } from "./mapDestinationChange";
import { relevantMapIssues } from "./mapEvidence";
import { overlappingPages } from "./mapOverlap";
import type {
  MapChange,
  MapCurrentGroup,
  MapPageFact,
  MapVocabulary,
} from "./mapTypes";

const CONFIDENCE_ORDER = { strong: 0, some: 1, limited: 2 } as const;

export function deriveMapChanges(input: {
  data: OverviewResponse;
  facts: MapPageFact[];
  groups: MapCurrentGroup[];
  vocabulary: MapVocabulary;
}): MapChange[] {
  const clusterContext = clusterContextForPages(input.data.pages);
  const candidates = input.groups.flatMap((group) =>
    changesForGroup(
      input.data,
      input.facts,
      group,
      input.vocabulary,
      clusterContext,
    ),
  );
  return [
    ...new Map(
      candidates.map((change) => [change.outcomeKey, change]),
    ).values(),
  ]
    .sort(
      (left, right) =>
        CONFIDENCE_ORDER[left.confidence] -
          CONFIDENCE_ORDER[right.confidence] ||
        Number(left.status === MAP_CHANGE_STATUS.UNRESOLVED) -
          Number(right.status === MAP_CHANGE_STATUS.UNRESOLVED) ||
        right.pages.length - left.pages.length ||
        left.title.localeCompare(right.title),
    )
    .slice(0, 6);
}

function changesForGroup(
  data: OverviewResponse,
  allFacts: MapPageFact[],
  group: MapCurrentGroup,
  vocabulary: MapVocabulary,
  clusterContext: ReturnType<typeof clusterContextForPages>,
): MapChange[] {
  if (group.key === "home" && group.facts.length === 1) return [];
  const rows: MapChange[] = [];
  const overlap = overlappingPages(group, data);
  if (overlap) {
    const facts = factsForPages(
      group.facts,
      overlap.pages.map((page) => page.id),
    );
    rows.push(
      consolidateChange(
        changeInput(
          data,
          group,
          facts,
          MAP_CHANGE_TYPE.CONSOLIDATE_PAGES,
          vocabulary,
        ),
        overlap.reason,
      ),
    );
  }

  if (group.facts.length >= 3 && !hasSectionHub(group, clusterContext)) {
    rows.push(
      createHubChange(
        changeInput(
          data,
          group,
          group.facts,
          MAP_CHANGE_TYPE.CREATE_HUB,
          vocabulary,
        ),
      ),
    );
    return rows;
  }

  const destinationIssues = relevantMapIssues(
    MAP_CHANGE_TYPE.CLARIFY_DESTINATION,
    group.facts,
  );
  const clarifyFacts = affectedFactsForIssues(
    data,
    group.facts,
    destinationIssues.map((issue) => issue.id),
  );
  const destinations = relatedGroupLabels(data, clarifyFacts, allFacts);
  const brokenLinks = brokenOutboundLinks(data, clarifyFacts);
  if (
    clarifyFacts.length > 0 &&
    destinations[0] &&
    (destinationIssues.length > 0 || brokenLinks > 0)
  ) {
    rows.push(
      clarifyDestinationChange(
        {
          data,
          group,
          facts: clarifyFacts,
          issues: relevantMapIssues(
            MAP_CHANGE_TYPE.CLARIFY_DESTINATION,
            clarifyFacts,
          ),
          vocabulary,
        },
        destinations[0],
        brokenLinks,
      ),
    );
    return rows;
  }

  const reorganiseIssues = relevantMapIssues(
    MAP_CHANGE_TYPE.REORGANISE_GROUP,
    group.facts,
  );
  const roles = new Set(group.facts.map((fact) => fact.role));
  if (
    group.facts.length >= 4 &&
    roles.size > 1 &&
    reorganiseIssues.length > 0
  ) {
    rows.push(
      reorganiseGroupChange({
        data,
        group,
        facts: group.facts,
        issues: reorganiseIssues,
        vocabulary,
      }),
    );
    return rows;
  }

  const roleGroups = groupByRole(group.facts)
    .map((facts) => {
      const issues = relevantMapIssues(MAP_CHANGE_TYPE.IMPROVE_PATTERN, facts);
      return {
        facts: affectedFactsForIssues(
          data,
          facts,
          issues.map((issue) => issue.id),
        ),
        issues,
      };
    })
    .filter(
      ({ facts, issues }) =>
        issues.length > 0 &&
        (facts.length > 1 ||
          issues.some(
            (issue) =>
              issue.severity === ISSUE_SEVERITY.CRITICAL ||
              issue.severity === CORE_WEB_VITALS_CONFIDENCE_LEVEL.HIGH,
          )),
    )
    .sort(
      (left, right) =>
        right.issues.length - left.issues.length ||
        right.facts.length - left.facts.length,
    );
  const pattern = roleGroups[0];
  if (pattern) {
    rows.push(
      improvePatternChange({
        data,
        group,
        facts: pattern.facts,
        issues: pattern.issues,
        vocabulary,
      }),
    );
    return rows;
  }

  if (groupNeedsClientContext(group)) {
    rows.push(
      unresolvedChange({
        data,
        group,
        facts: group.facts,
        issues: [],
        vocabulary,
      }),
    );
  }
  return rows;
}

function changeInput(
  data: OverviewResponse,
  group: MapCurrentGroup,
  facts: MapPageFact[],
  type: MapChange["type"],
  vocabulary: MapVocabulary,
) {
  return {
    data,
    group,
    facts,
    issues: relevantMapIssues(type, facts),
    vocabulary,
  };
}
